import { Map, Set } from "immutable";
import { createSelector } from "reselect";

const getSuperAndChildrenById = state => state.data.superAndChildrenById;

const getChildrenRecursive = (superAndChildrenById, id, result) => {
  // First, grab the children of this item
  const nextGen = superAndChildrenById.get(id).children;
  // Convert the children to a Set, and use withMutations to update it with
  // recursive calls to grab all of their children
  result[id] = nextGen.toSet().withMutations(descendants => {
    nextGen.forEach(oId => {
      descendants.union(getChildrenRecursive(superAndChildrenById, oId, result));
    });
  });
  return result[id];
};

/**
 * @param {Map} superAndChildrenById
 */
const getDescendantsMapFromSuperAndChildren = createSelector(
  // input
  superAndChildrenById => superAndChildrenById,
  // output
  superAndChildrenById => {
    let result = {};
    // iterate through all top-level items
    // The children will be handled recursively
    superAndChildrenById
      .filter(i => !i.super)
      .forEach((i, id) => getChildrenRecursive(superAndChildrenById, id, result));
    return Map(result);
  }
);

const getDescendantsMapFromState = state => getDescendantsMapFromSuperAndChildren(getSuperAndChildrenById(state));

const getDescendantSetFromMapForIds = (descendantsById, ids) => {
  return Set().withMutations(tmp => {
    ids.forEach(id => {
      const descendant = descendantsById.get(id);
      if (descendant) {
        tmp.union(descendant);
      }
    });
  });
};

const getDescendantSetForIds = (state, ids) => {
  const descendantsById = getDescendantsMapFromState(state);
  return getDescendantSetFromMapForIds(descendantsById, ids);
};

const accumulateAncestorsRecursive = (superAndChildrenById, id, ancestors, result) => {
  // First, save the ancestors passed down to here.
  result[id] = ancestors;
  // Now add my own ID ready to pass to my children
  const nextAncestors = ancestors.add(id);
  // Now call for all children, passing along the set of their ancestors
  superAndChildrenById.get(id).children.forEach(oId => {
    accumulateAncestorsRecursive(superAndChildrenById, oId, nextAncestors, result);
  });
};

/**
 * @param {Map} superAndChildrenById
 */
const getAncestorsMapFromSuperAndChildren = createSelector(
  // input
  superAndChildrenById => superAndChildrenById,
  // output
  superAndChildrenById => {
    let result = {};
    // iterate through all top-level items
    // The children will be handled recursively
    superAndChildrenById
      .filter(i => !i.super)
      .forEach((i, id) => accumulateAncestorsRecursive(superAndChildrenById, id, Set(), result));
    return Map(result);
  }
);

const getAncestorsMapFromState = state => getAncestorsMapFromSuperAndChildren(getSuperAndChildrenById(state));

export default {
  getSuperAndChildrenById,
  getDescendantsMapFromSuperAndChildren,
  getDescendantsMapFromState,
  getDescendantSetForIds,
  getDescendantSetFromMapForIds,
  getAncestorsMapFromSuperAndChildren,
  getAncestorsMapFromState
};
